import { useCallback } from 'react'

import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { Group, TextInput } from '~/components/component-library'
import { StepButtons } from '~/components/create-workspace/StepButtons'
import { PhotoEdit } from '~/components/settings/PhotoEdit'
import {
  useActiveOrgMember,
  useActiveOrgUpdates,
  useRequiredActiveOrg,
} from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { validateOrgName } from '~/utils/org'
import { sendTrackingData } from '~/utils/tracking'

interface WorkspaceFormValues {
  name: string
  logoUrl?: string
}

export function WorkspaceStep({ stepForward }: { stepForward: () => void }) {
  const activeOrg = useRequiredActiveOrg()
  const orgMember = useActiveOrgMember()
  const isOwner = orgMember?.role === 'OWNER'

  const { serverPostRequest, isProcessing: isProcessingCreate } =
    useServerRequests()
  const { serverPutRequest, isProcessing: isProcessingEdit } =
    useServerRequests()

  const { updateName, updateLogoUrl } = useActiveOrgUpdates()

  const { control, handleSubmit, setValue } = useForm<WorkspaceFormValues>({
    defaultValues: {
      name: activeOrg?.name ?? '',
      logoUrl: activeOrg?.logoUrl,
    },
  })

  const logoUrl = useWatch({ control, name: 'logoUrl' })
  const name = useWatch({ control, name: 'name' })

  const onSubmit: SubmitHandler<WorkspaceFormValues> = useCallback(
    async ({ name }) => {
      sendTrackingData('submitNewOrgModal')

      if (!isOwner) {
        toast.error('You are not authorized to edit this workspace')
      }

      const result = await serverPutRequest({
        path: '/api/org/edit/name',
        params: {
          orgId: activeOrg!.id,
          name: name,
        },
        errorMessage: 'Failed to update name',
      })

      if (result) {
        updateName(name)
        stepForward()
      }
    },
    [isOwner, activeOrg, stepForward, serverPutRequest, updateName],
  )

  const uploadLogo = useCallback(
    async (file: File) => {
      const formData = new FormData()
      formData.append('logo', file)
      formData.append('orgId', activeOrg!.id)

      const result = await serverPostRequest<{
        logoUrl: string
      }>({
        path: '/api/org/edit/logo/upload',
        params: formData,
        errorMessage: 'Failed to update logo',
      })

      if (result) {
        setValue('logoUrl', result.data.logoUrl)
        updateLogoUrl(result.data.logoUrl)
      }
    },
    [activeOrg, updateLogoUrl, serverPostRequest, setValue],
  )

  return (
    <>
      <TextInput
        label="Workspace name"
        control={control}
        inputName={'name'}
        placeholder={'Splits Labs'}
        validationFunc={validateOrgName}
        autoFocus
        disabled={!isOwner}
      />
      <Group
        label="Workspace logo"
        footer="Click to upload a logo for your workspace"
      >
        <PhotoEdit
          photoUrl={logoUrl ?? ''} // Provide fallback for potentially undefined value
          photoAlt={'Workspace logo'}
          processFile={uploadLogo}
          shape="rounded"
          size="md"
        />
      </Group>
      <StepButtons
        onNextClick={handleSubmit(onSubmit)}
        isDisabled={
          isProcessingCreate || isProcessingEdit || !name?.trim() || !logoUrl
        }
      />
    </>
  )
}
